import React, { PropsWithChildren, useMemo, useRef, useState } from 'react';
import classes from './TableSchema.module.scss';
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { Switch } from 'new/elements/Switch';
import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import { ReactComponent as ArrowRight } from 'new/elements/flows/assets/arrow-small-right.svg';
import { ReactComponent as DatabaseIcon } from 'new/elements/flows/Databases/assets/database.svg';
import { ReactComponent as DoubleArrowRightIcon } from 'new/elements/flows/assets/doubleArrowRight.svg';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { TextField } from 'new/elements/TextField';
import { Button } from 'new/elements/Button';
import { EditRounded } from '@mui/icons-material';
import { MappingType } from 'new/elements/flows/Databases/useDatabases';
import { isMatch } from 'lodash';

interface TableSchemaProps {
  table?: { children: any[]; name: string };
  mapping?: MappingType[string];
  onFinishSchema?: (schema: any[]) => void;
  sourceConnector?: any;
  sinkConnector?: any;
  onChangeMapping?: (map: MappingType[string]) => void;
  deleteMapping?: (tableName: string) => void;
}

const Resource = ({
  name,
  active,
  connectorType,
  resourceType,
}: {
  name?: string;
  active?: boolean;
  connectorType?: string;
  resourceType: 'SINK' | 'SOURCE';
}) => {
  const metas = useSelector((state: any) => state.paging.metaInfoById);
  const meta = metas[connectorType];
  return (
    <div
      className={cx(classes.resourceContainer, {
        [classes.resourceContainer_active]: active,
      })}
    >
      <div className={classes.resourceImgContainer}>
        {meta?.iconSm ? <img src={meta.iconSm} /> : <DatabaseIcon />}
      </div>
      <Stack>
        <Typography variant="overline">
          {resourceType === 'SOURCE' ? 'SOURCE' : 'DESTINATION'} DB
        </Typography>
        <Typography variant="body2">{name || 'Configured in next steps'}</Typography>
      </Stack>
    </div>
  );
};

function FieldName({
  state,
  title,
  originalName,
  updateRow,
}: {
  title: string;
  state?: 'edited' | 'added';
  originalName?: string;
  updateRow?: (row: string, field: string, value: string | boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const textRef = useRef<HTMLInputElement>(null);

  const onClickEdit = () => {
    setEditing(true);
  };

  const onClickSave = () => {
    setEditing(false);
    updateRow(originalName, 'name', textRef.current.value);
  };

  if (editing) {
    return (
      <div className={classes.fieldNameEditContainer}>
        <TextField
          inputProps={{
            'data-testid': `input-field-name-${title}`,
          }}
          inputRef={textRef}
          defaultValue={title}
        />
        <Button
          data-testid={`save-field-name-${title}`}
          onClick={onClickSave}
          variant="outlined"
          color="primary"
        >
          Save
        </Button>
      </div>
    );
  }

  if (state === 'edited') {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        className={classes.fieldNameChangeContainer}
      >
        <Typography>{originalName}</Typography>
        <Tooltip title="Will be changed to">
          <DoubleArrowRightIcon />
        </Tooltip>
        <Typography>{title}</Typography>
        <Tooltip title="Edit">
          <IconButton
            data-testid={`edit-field-name-${title}`}
            onClick={onClickEdit}
            className={classes.editButton}
          >
            <EditRounded />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack data-testid={`field-name-${title}`} direction="row" alignItems="center" gap="16px">
      <Typography>{title}</Typography>
      <Tooltip title="Edit">
        <IconButton
          data-testid={`edit-field-name-${title}`}
          onClick={onClickEdit}
          className={classes.editButton}
        >
          <EditRounded />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function getInitialMapping(table) {
  return (
    table.children?.reduce?.((map, column) => {
      map[column.name] = {
        checked: true,
        primary: false,
        name: column.name,
      };
      return map;
    }, {}) || {}
  );
}

export const TableSchema = (props: TableSchemaProps) => {
  const { table, onChangeMapping, deleteMapping, sourceConnector, sinkConnector, mapping } = props;
  const initialMapping = useMemo(() => getInitialMapping(table), [table]);
  const [rowMap, setRowMap] = useState<MappingType[string]>(mapping || {});

  const updateRow = (row, field, value) => {
    setRowMap((map) => {
      const newMap = { ...map };
      newMap[row] = {
        name: row,
        ...newMap[row],
        [field]: value,
      };
      const match = isMatch(initialMapping, newMap);
      if (match) {
        deleteMapping(table.name);
        return {};
      } else {
        onChangeMapping(newMap);
        return newMap;
      }
    });
  };

  const onChangeAll = (e) => {
    table.children?.map((row) => {
      updateRow(row.name, 'checked', e.target.checked);
    });
  };

  const onChangePrimary = (row) => (e) => {
    updateRow(row.name, 'primary', e.target.checked);
  };

  const onChangeChecked = (row) => (e) => {
    updateRow(row.name, 'checked', e.target.checked);
  };

  const allChecked =
    table?.children?.filter((row) => rowMap[row.name]?.checked !== false)?.length ===
    table.children?.length;

  return (
    <div className={classes.container}>
      <div className={classes.top}>
        <FormControlLabel
          control={
            <Switch checked={allChecked} onChange={onChangeAll} size="small" color="primary" />
          }
          label={'Sync all'}
          labelPlacement="end"
        />
        <Stack alignItems="center" justifyContent="center" flex={1}>
          <Typography>Source namespace: {table.name}</Typography>
        </Stack>
        <Stack flex={1} />
      </div>
      <div className={classes.resources}>
        <Stack
          direction="row"
          flex={1}
          paddingRight="20px"
          justifyContent="space-between"
          alignItems="center"
        >
          <Resource connectorType={sourceConnector} resourceType="SOURCE" />
          <ArrowRight />
        </Stack>
        <Stack flexBasis="calc(35% - 10px)" gap="35px" direction="row" alignItems="center">
          <Resource connectorType={sinkConnector} resourceType="SINK" />
        </Stack>
      </div>
      <MuiTable size="small" className={classes.muiTable}>
        <TableHead>
          <TableRow>
            <TableCell>Column name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Primary key</TableCell>
            <TableCell sx={{ width: '35%' }}>Field name</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {table?.children?.map((row) => {
            const state =
              !!rowMap[row.name]?.name && row.name !== rowMap[row.name]?.name ? 'edited' : 'added';
            return (
              <TableRow>
                <TableCell component="th" scope="row" className={classes.columnName}>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={onChangeChecked(row)}
                        checked={rowMap[row.name]?.checked !== false}
                        size="small"
                        color="primary"
                      />
                    }
                    label={'Sync ' + row.name}
                    labelPlacement="end"
                  />
                </TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>
                  <div className={classes.primaryKey}>
                    <Checkbox
                      onChange={onChangePrimary(row)}
                      checked={rowMap[row.name] ? rowMap[row.name]?.primary : row.primary}
                      readOnly
                    />
                    {state === 'edited' ? <ArrowRight /> : <ArrowRight />}
                  </div>
                </TableCell>
                <TableCell className={classes.columnFieldName}>
                  <FieldName
                    title={rowMap[row.name]?.name || row.name}
                    state={state}
                    updateRow={updateRow}
                    originalName={row.name}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </MuiTable>
    </div>
  );
};
